import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styled from 'styled-components';
import { RiDraggable } from 'react-icons/ri';

const LayerItemWrapper = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 4px;
	transition: background-color 0.15s ease;
	border-radius: 4px;
	padding: 2px;
	margin: -2px;

	&:hover {
		background-color: ${p => p.theme.colors.backgroundAccent || 'rgba(255, 255, 255, 0.05)'};
	}
`;

const DragHandle = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 4px 2px;
	background: transparent;
	border: none;
	cursor: grab;
	color: ${p => p.theme.colors.textAccent || '#888'};
	opacity: 0.5;
	transition:
		opacity 0.15s ease,
		color 0.15s ease;
	flex-shrink: 0;

	&:hover {
		opacity: 1;
		color: ${p => p.theme.colors.textColor || '#fff'};
	}

	&:active {
		cursor: grabbing;
	}

	svg {
		font-size: 1rem;
	}
`;

const LayerContent = styled.div`
	flex: 1;
	min-width: 0;
	overflow: hidden;
`;

/**
 * Sortable wrapper for individual layer items with a drag handle.
 *
 * When `isReady` is false, renders children without the sortable wrapper,
 * allowing the layer component to initialize and add itself to the map.
 * Once ready, the full sortable wrapper with drag handle is rendered.
 *
 * Uses CSS.Translate instead of CSS.Transform to prevent stretching
 * artifacts during drag operations.
 *
 * @param {Object} props
 * @param {string} props.id - Unique identifier for the sortable item (must match SortableContext items)
 * @param {boolean} [props.isReady=true] - Whether the layer is ready on the map (controls drag handle visibility)
 * @param {React.ReactNode} props.children - Layer component to render
 */
const SortableLayerItem = ({ id, isReady = true, children }) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id,
		disabled: !isReady,
	});

	// Use Translate (not Transform) to avoid scaleX/scaleY which causes stretching
	const style = {
		transform: CSS.Translate.toString(transform),
		transition,
		opacity: isDragging ? 0.4 : 1,
		zIndex: isDragging ? 1000 : 'auto',
	};

	// Render without wrapper if not ready - allows layer component to mount and initialize
	if (!isReady) {
		return <>{children}</>;
	}

	return (
		<LayerItemWrapper ref={setNodeRef} style={style}>
			<LayerContent>{children}</LayerContent>
			<DragHandle type="button" {...attributes} {...listeners} title="Drag to reorder">
				<RiDraggable />
			</DragHandle>
		</LayerItemWrapper>
	);
};

export default SortableLayerItem;
